import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import Modal from "@/components/ui/modal";
import Button from "@/components/ui/button";
import Input from "@/components/ui/input";
import Dropdown from "@/components/ui/dropdown";
import { Pencil, Close as CloseIcon } from "@/components/icons";
import { staffManagementApi } from "../services/staff-management.api";
import { maskTurkishPhone } from "../utils/phoneMask";
import type { Staff } from "../types/staff-management.types";
import { normalizeStaffGender } from "../utils/normalizeStaffGender";
import { validatePassword } from "@/core/auth/utils/password.utils";
import styles from "./EditStaffModal.module.scss";
import { toast } from "sonner";

// GENDER_OPTIONS component içinde t() ile üretiliyor — module-level array
// hardcoded TR oluyordu ve EN modunda da "Erkek/Kadın" görünüyordu.

interface EditStaffFormData {
  tcId: string;
  name: string;
  phone: string;
  email: string;
  gender: "male" | "female" | null;
  title: string;
  /** Personel sicil numarası */
  sicilNo: string;
  ldapUsername: string;
  locationId: string;
  departmentId: string;
  /** Yeni şifre — boş bırakılırsa şifre güncellenmez. */
  password: string;
}

interface EditStaffModalProps {
  isOpen: boolean;
  onClose: () => void;
  staff: Staff | null;
  onConfirm?: (staffId: string, data: EditStaffFormData) => void;
  /** Şifre alanının görünür olması için yetki gerekir (EDUCATION_MANAGER). */
  canEditPassword?: boolean;
}

const EditStaffModal: React.FC<EditStaffModalProps> = ({
  isOpen,
  onClose,
  staff,
  onConfirm,
  canEditPassword = false,
}) => {
  const { t } = useTranslation();
  const GENDER_OPTIONS = useMemo(
    () => [
      { label: t("staffManagement.gender.male"), value: "male" },
      { label: t("staffManagement.gender.female"), value: "female" },
      {
        label: t("staffManagement.gender.unspecified"),
        value: "",
      },
    ],
    [t],
  );
  const [departmentOptions, setDepartmentOptions] = useState<
    { label: string; value: string }[]
  >([]);
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState<EditStaffFormData>({
    tcId: staff?.identityNumber ?? "",
    name: staff?.name ?? "",
    phone: maskTurkishPhone(staff?.phoneNumber ?? ""),
    email: staff?.email ?? "",
    gender: normalizeStaffGender(staff?.gender),
    title: staff?.title ?? "",
    sicilNo: staff?.sicilNo ?? "",
    ldapUsername: staff?.ldapUsername ?? "",
    locationId: staff?.locationId ?? "",
    departmentId: staff?.departmentId ?? "",
    password: "",
  });
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (isOpen && staff) {
      setFormData({
        tcId: staff.identityNumber ?? "",
        name: staff.name ?? "",
        phone: maskTurkishPhone(staff.phoneNumber ?? ""),
        email: staff.email ?? "",
        gender: normalizeStaffGender(staff.gender),
        title: staff.title ?? "",
        sicilNo: staff.sicilNo ?? "",
        ldapUsername: staff.ldapUsername ?? "",
        locationId: staff.locationId ?? "",
        departmentId: staff.departmentId ?? "",
        password: "",
      });
      setShowPassword(false);
      setCurrentStep(0);
    }
  }, [isOpen, staff]);

  useEffect(() => {
    if (!isOpen) return;

    staffManagementApi
      .getDepartments()
      .then((departments) => {
        setDepartmentOptions(
          departments.map((d) => ({ label: d.name, value: d.id })),
        );
      })
      .catch(() => {
        setDepartmentOptions([]);
      });
  }, [isOpen]);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const tc = formData.tcId.trim();
    const phone = formData.phone.trim();
    const tcRegex = /^[1-9]\d{10}$/;
    if (tc && !tcRegex.test(tc)) {
      toast.error(
        t("staffManagement.editModal.tcInvalid"),
      );
      return;
    }
    if (!phone) {
      toast.error(
        t("staffManagement.editModal.phoneRequired"),
      );
      return;
    }
    // Şifre yalnızca dolu girildiyse doğrula; boşsa güncelleme yapılmaz.
    if (canEditPassword && formData.password.trim().length > 0) {
      const validation = validatePassword(formData.password);
      if (!validation.isValid) {
        toast.error(
          t(
            validation.error ?? "login.passwordTooShort",
            t("staffManagement.editModal.passwordTooShort"),
          ),
        );
        return;
      }
    }
    if (staff && onConfirm) {
      onConfirm(staff.id, formData);
    }
    // onClose'u burada çağırmıyoruz: parent (onConfirm) async; başarılı olursa
    // kendisi setEditStaff(null) ile modal'ı kapatıyor. Burada erken kapatırsak
    // 409 (mükerrer e-posta vb.) hatasında modal sessizce kapanır ve kullanıcı
    // form datasını kaybeder.
  };

  const customHeader = (
    <>
      <div className={styles.modalHeader}>
        <div className={styles.modalHeaderLeft}>
          <Pencil width={24} height={24} color="#14181F" />
          <h3 className={styles.modalTitle}>
            {t("staffManagement.editModal.title")}
          </h3>
        </div>
        <button
          type="button"
          className={styles.closeButton}
          onClick={onClose}
          aria-label={t("common.close")}
        >
          <CloseIcon width={20} height={20} color="#72808f" />
        </button>
      </div>
      <div className={styles.stepTabs}>
        <div className={styles.stepTabWrapper}>
          <span
            className={`${styles.stepTab} ${currentStep === 0 ? styles.active : ""}`}
          >
            <span className={styles.stepNumber}>1</span>
            {t("staffManagement.editModal.step1")}
          </span>
        </div>
        <div className={styles.stepTabWrapper}>
          <span
            className={`${styles.stepTab} ${currentStep === 1 ? styles.active : ""}`}
          >
            <span className={styles.stepNumber}>2</span>
            {t("staffManagement.editModal.step2")}
          </span>
        </div>
      </div>
      <div className={styles.stepProgressFull}>
        <div
          className={styles.stepProgressFullBar}
          style={{
            width: `${((currentStep + 1) / 2) * 100}%`,
          }}
        />
      </div>
    </>
  );

  const step1Content = (
    <div className={styles.sectionedForm}>
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionBadge}>1</span>
          <div>
            <h4 className={styles.sectionTitle}>
              {t("staffManagement.addModal.sectionIdentity")}
            </h4>
            <p className={styles.sectionSubtitle}>
              {t("staffManagement.addModal.sectionIdentityHint")}
            </p>
          </div>
        </div>
        <div className={styles.grid2}>
          <Input
            label={t("staffManagement.editModal.personnelName")}
            value={formData.name}
            maxLength={101}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, name: e.target.value }))
            }
            placeholder={t("staffManagement.editModal.personnelNamePlaceholder")}
          />
          <Input
            label={t("staffManagement.editModal.tcId")}
            value={formData.tcId}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, tcId: e.target.value }))
            }
            placeholder="12345678901"
          />
          <Dropdown
            label={t("staffManagement.editModal.gender")}
            placeholder={t("staffManagement.editModal.genderPlaceholder")}
            options={GENDER_OPTIONS}
            value={formData.gender ?? ""}
            selectedLabel={
              formData.gender
                ? GENDER_OPTIONS.find((o) => o.value === formData.gender)?.label
                : undefined
            }
            onSelect={(value) => {
              const raw = Array.isArray(value) ? value[0] : value;
              const normalized =
                raw === "male" || raw === "female" ? raw : null;
              setFormData((prev) => ({
                ...prev,
                gender: normalized,
              }));
            }}
          />
        </div>
      </section>

      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionBadge}>2</span>
          <div>
            <h4 className={styles.sectionTitle}>
              {t("staffManagement.addModal.sectionContact")}
            </h4>
            <p className={styles.sectionSubtitle}>
              {t("staffManagement.addModal.sectionContactHint")}
            </p>
          </div>
        </div>
        <div className={styles.grid2}>
          <Input
            label={t("staffManagement.editModal.phone")}
            value={formData.phone}
            onChange={(e) =>
              setFormData((prev) => ({
                ...prev,
                phone: maskTurkishPhone(e.target.value),
              }))
            }
            placeholder="+90 5XX XXX XX XX"
            inputMode="tel"
            maxLength={17}
            required
          />
          <Input
            label={t("staffManagement.editModal.email")}
            type="email"
            value={formData.email}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, email: e.target.value }))
            }
            placeholder="user@example.com"
          />
        </div>
      </section>

      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionBadge}>3</span>
          <div>
            <h4 className={styles.sectionTitle}>
              {t("staffManagement.addModal.sectionAccount")}
            </h4>
            <p className={styles.sectionSubtitle}>
              {t("staffManagement.addModal.sectionAccountHint")}
            </p>
          </div>
        </div>
        <div className={styles.grid2}>
          <Input
            label={t("staffManagement.editModal.jobTitle")}
            value={formData.title}
            maxLength={100}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, title: e.target.value }))
            }
            placeholder={t("staffManagement.addModal.titlePlaceholder")}
          />
          <Input
            label={t("common.sicilNo")}
            value={formData.sicilNo}
            maxLength={50}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, sicilNo: e.target.value }))
            }
            placeholder={t("staffManagement.editModal.sicilNoPlaceholder")}
          />
          <Input
            label={t("staffManagement.editModal.ldapUsername")}
            value={formData.ldapUsername}
            maxLength={100}
            onChange={(e) =>
              setFormData((prev) => ({
                ...prev,
                ldapUsername: e.target.value,
              }))
            }
            placeholder={t("staffManagement.editModal.ldapUsernamePlaceholder")}
          />
          {canEditPassword && (
            <div className={styles.passwordField}>
              <Input
                label={t("staffManagement.editModal.password")}
                type={showPassword ? "text" : "password"}
                value={formData.password}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, password: e.target.value }))
                }
                placeholder={t("staffManagement.editModal.passwordPlaceholder")}
                autoComplete="new-password"
              />
              <div className={styles.passwordRow}>
                <span className={styles.passwordHint}>
                  {t("staffManagement.editModal.passwordHint")}
                </span>
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className={styles.passwordToggle}
                >
                  {showPassword
                    ? t("common.hide")
                    : t("common.show")}
                </button>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );

  const step2Content = (
    <div className={styles.sectionedForm}>
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionBadge}>1</span>
          <div>
            <h4 className={styles.sectionTitle}>
              {t("staffManagement.addModal.sectionOrganization")}
            </h4>
            <p className={styles.sectionSubtitle}>
              {t("staffManagement.addModal.sectionOrganizationHint")}
            </p>
          </div>
        </div>
        <div className={styles.grid2}>
          <Dropdown
            label={t("staffManagement.editModal.department")}
            placeholder={t("staffManagement.filters.departmentPlaceholder")}
            searchable
            options={departmentOptions}
            value={formData.departmentId}
            selectedLabel={
              departmentOptions.find((o) => o.value === formData.departmentId)
                ?.label
            }
            onSelect={(value) =>
              setFormData((prev) => ({
                ...prev,
                departmentId: Array.isArray(value)
                  ? (value[0] ?? "")
                  : (value ?? ""),
              }))
            }
          />
        </div>
      </section>
    </div>
  );

  const customFooter = (
    <div className={styles.footerWrapper}>
      <div className={styles.footerButtons}>
        {currentStep === 0 ? (
          <Button
            variant="text"
            color="secondary"
            type="button"
            onClick={onClose}
          >
            {t("staffManagement.editModal.cancel")}
          </Button>
        ) : (
          <Button
            variant="text"
            color="secondary"
            type="button"
            onClick={() => setCurrentStep(0)}
          >
            {t("staffManagement.editModal.back")}
          </Button>
        )}
        {currentStep === 0 ? (
          <Button
            variant="contained"
            color="primary"
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setCurrentStep(1);
            }}
          >
            {t("staffManagement.editModal.next")}
          </Button>
        ) : (
          <Button
            variant="contained"
            color="primary"
            type="submit"
            form="editStaffForm"
          >
            {t("staffManagement.editModal.update")}
          </Button>
        )}
      </div>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t("staffManagement.editModal.title")}
      customHeader={customHeader}
      customFooter={customFooter}
      isMultiStep
      hideStepProgress
      currentStep={currentStep}
      stepContent={[step1Content, step2Content]}
      onStepChange={setCurrentStep}
      submitButtonText={t("staffManagement.editModal.update")}
      handleSubmit={handleSubmit}
      withForm
      formId="editStaffForm"
      size="lg"
    >
      {null}
    </Modal>
  );
};

export default EditStaffModal;
